import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Loader2, ExternalLink, AlertTriangle, BookOpen, Bookmark, BookmarkCheck, Trash2 } from "lucide-react";
import { Disclaimer } from "@/components/Badges";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Illinois small claims jurisdictional limit",
  "Statute of limitations on credit card debt in Illinois",
  "Motion to vacate default judgment",
  "Landlord duty to mitigate damages",
  "Wage garnishment exemptions",
];

export default function CaseLaw() {
  const { user } = useAuth();
  const signedIn = user && typeof user === "object";
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [bookmarks, setBookmarks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!signedIn) { setBookmarks([]); return; }
    api.get("/case-law/bookmarks")
      .then((r) => setBookmarks(r.data.items || []))
      .catch((err) => console.warn("bookmarks load failed", err));
  }, [signedIn]);

  const submit = async (text) => {
    const query = (text ?? q).trim();
    if (!query) return;
    setQ(query);
    setErr("");
    setResult(null);
    setLoading(true);
    try {
      const res = await api.post("/case-law/search", { query, jurisdiction_state: "IL" });
      setResult(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveBookmark = async () => {
    if (!signedIn || !result) return;
    setSaving(true);
    try {
      const { data } = await api.post("/case-law/bookmarks", { query: q, topic: result.topic || q });
      setBookmarks((b) => [data, ...b]);
      toast.success("Search saved to your bookmarks");
    } catch {
      toast.error("Could not save bookmark");
    } finally {
      setSaving(false);
    }
  };

  const removeBookmark = async (id) => {
    try {
      await api.delete(`/case-law/bookmarks/${id}`);
      setBookmarks((b) => b.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not remove bookmark");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-[#C85A32]" /> Search for case opinions
        </h1>
        <p className="text-[#4A525A] mt-1">
          Type a topic, statute, or legal question. Get a plain-English overview and links to search
          trustworthy free case-law databases.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm text-amber-800 flex gap-2 items-start" data-testid="caselaw-warning">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          AI-generated case-law summaries can be wrong. Any case name below is a starting point only — always
          verify the citation, court, year, and holding on an official source before relying on it.
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-col sm:flex-row gap-2"
      >
        <div className="relative flex-1">
          <Search className="w-5 h-5 text-[#707880] absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="caselaw-query"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try 'motion to vacate default judgment' or 'statute of limitations debt Illinois'"
            className="w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none pl-11 pr-4 py-3 bg-white"
          />
        </div>
        <button
          type="submit"
          disabled={loading || q.trim().length < 3}
          data-testid="caselaw-submit"
          className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-60 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </form>

      <div className="flex flex-wrap gap-2">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={s}
            data-testid={`caselaw-suggestion-${i}`}
            onClick={() => submit(s)}
            className="pod-focus text-xs bg-[#FAF5EC] hover:bg-[#F3EFE6] border border-[#E4DDD3] text-[#1C2024] px-3 py-1.5 rounded-full"
          >
            {s}
          </button>
        ))}
      </div>

      {signedIn && bookmarks.length > 0 && (
        <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5" data-testid="caselaw-bookmarks">
          <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold flex items-center gap-2">
            <BookmarkCheck className="w-4 h-4 text-[#C85A32]" /> Your saved searches
          </div>
          <ul className="mt-3 space-y-2">
            {bookmarks.map((b) => (
              <li key={b.id} data-testid={`bookmark-${b.id}`} className="flex items-center gap-3 bg-[#FAF5EC] border border-[#E4DDD3] rounded-xl px-3 py-2">
                <button
                  onClick={() => submit(b.query)}
                  data-testid={`bookmark-run-${b.id}`}
                  className="pod-focus flex-1 text-left text-[#1C2024] font-semibold hover:text-[#C85A32]"
                >
                  {b.topic || b.query}
                </button>
                <button
                  onClick={() => removeBookmark(b.id)}
                  data-testid={`bookmark-delete-${b.id}`}
                  className="pod-focus p-1.5 text-[#4A525A] hover:text-red-600 rounded-lg"
                  aria-label="Remove bookmark"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="caselaw-error">{err}</div>}

      {result && (
        <div className="space-y-4" data-testid="caselaw-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-lg font-semibold">{result.topic}</div>
            {signedIn && (
              <button
                onClick={saveBookmark}
                disabled={saving}
                data-testid="caselaw-save-bookmark"
                className="pod-focus text-sm border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] font-bold py-2 px-3 rounded-xl inline-flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
                Save this search
              </button>
            )}
          </div>
          {result.plain_explanation && <Card title="Plain-English overview"><p className="leading-relaxed">{result.plain_explanation}</p></Card>}
          <ListCard title="General legal principles" items={result.general_principles} />

          <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">Possibly relevant cases (verify before relying on any)</div>
            {result.possibly_relevant_cases.length === 0 ? (
              <p className="mt-3 text-[#4A525A]">
                No cases are listed here on purpose — verify any case law directly from a trusted database below.
              </p>
            ) : (
              <ul className="mt-3 space-y-3">
                {result.possibly_relevant_cases.map((c, i) => (
                  <li key={`${c.name}-${c.year}-${i}`} data-testid={`caselaw-case-${i}`} className="border border-[#E4DDD3] rounded-xl p-4 bg-[#FBF9F5]">
                    <div className="font-bold text-[#1C2024]">{c.name}</div>
                    <div className="text-xs text-[#4A525A] mono mt-0.5">{c.court}{c.year ? `, ${c.year}` : ""}</div>
                    {c.issue_plain && <div className="mt-2">{c.issue_plain}</div>}
                    <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2 items-start">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{c.verify_note}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5" data-testid="caselaw-links">
            <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">Search these trusted databases</div>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {result.search_here.map((lk, i) => (
                <li key={lk.url}>
                  <a
                    href={lk.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`caselaw-link-${i}`}
                    className="pod-focus block bg-[#FAF5EC] hover:bg-[#F3EFE6] border border-[#E4DDD3] rounded-xl p-3 text-[#1C2024]"
                  >
                    <div className="font-bold flex items-center gap-2">{lk.name} <ExternalLink className="w-3.5 h-3.5" /></div>
                    <div className="text-xs text-[#4A525A] break-all mt-0.5">{lk.url}</div>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <ListCard title="Questions to ask an attorney" items={result.questions_to_ask_attorney} />

          <Disclaimer>{result.disclaimer}</Disclaimer>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function ListCard({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{title}</div>
      <ul className="mt-3 space-y-2">
        {items.map((it, idx) => (
          <li key={idx} className="flex gap-3 leading-relaxed">
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#FAF5EC] text-[#C85A32] font-bold text-sm flex items-center justify-center">{idx + 1}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
